import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "../ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, loading } = useAuth();

  const mainNavItems: Array<{ label: string; to: string }> = [
    { label: "Product", to: "/#overview" },
    { label: "Features", to: "/#features" },
    { label: "How It Works", to: "/#how_it_works" },
    { label: "Blog", to: "/blog" },
  ];

  const personaNavItems: Array<{ label: string; to: string; description?: string }> = [
    { label: "Player", to: "/player" },
    { label: "Coach", to: "/coach" },
    { label: "Club Owner", to: "/club-owner" },
    { label: "Parent", to: "/parent" },
    { label: "Venue Owner", to: "/venue-owner" },
    { label: "Tournament Organizer", to: "/tournament-organizer" },
    { label: "Fan", to: "/fan" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-background/90 backdrop-blur-lg shadow-lg border-b border-border/50"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <BrandLogo
              to="/"
              size="md"
              className="text-foreground hover:text-accent-soft transition-colors"
            />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/#overview"
              className="text-foreground/80 hover:text-foreground transition-colors text-sm font-medium"
            >
              Product
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center gap-1 text-foreground/80 hover:text-foreground transition-colors text-sm font-medium">
                  Who It&apos;s For <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {personaNavItems.map((item) => (
                  <DropdownMenuItem key={item.to} asChild>
                    <Link to={item.to} className="w-full">
                      {item.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {mainNavItems
              .filter((i) => i.label !== "Product")
              .map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  className="text-foreground/80 hover:text-foreground transition-colors text-sm font-medium"
                >
                  {item.label}
                </Link>
              ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center space-x-4">
            <ThemeToggle variant="icon" />
            {!loading && user ? (
              <Button asChild className="bg-accent hover:bg-accent-soft">
                <Link to="/dashboard">Go to Dashboard</Link>
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  asChild
                  className="text-foreground/90 hover:text-foreground hover:bg-surface/50"
                >
                  <Link to="/auth">Login</Link>
                </Button>
                <Button asChild className="bg-accent hover:bg-accent-soft">
                  <Link to="/auth?tab=signup">Get Started</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-foreground hover:text-accent transition-colors"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-surface/95 backdrop-blur-lg border-t border-border">
            <div className="flex flex-col space-y-4">
              <Link
                to="/#overview"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground/90 hover:text-foreground transition-colors text-sm font-medium text-left"
              >
                Product
              </Link>
              <Link
                to="/#features"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground/90 hover:text-foreground transition-colors text-sm font-medium text-left"
              >
                Features
              </Link>
              <Link
                to="/#how_it_works"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground/90 hover:text-foreground transition-colors text-sm font-medium text-left"
              >
                How It Works
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-foreground/90 hover:text-foreground transition-colors text-sm font-medium text-left"
              >
                Blog
              </Link>

              <div className="pt-2 border-t border-border/60" />
              <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                Who It&apos;s For
              </div>
              {personaNavItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-foreground/90 hover:text-foreground transition-colors text-sm font-medium text-left"
                >
                  {item.label}
                </Link>
              ))}

              <div className="pt-4 flex flex-col space-y-2">
                <div className="flex justify-center pb-2">
                  <ThemeToggle variant="icon" />
                </div>
                {!loading && user ? (
                  <Button
                    asChild
                    className="w-full bg-accent hover:bg-accent-soft"
                  >
                    <Link to="/dashboard">Go to Dashboard</Link>
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      asChild
                      className="w-full border-border hover:bg-surface"
                    >
                      <Link to="/auth">Login</Link>
                    </Button>
                    <Button
                      asChild
                      className="w-full bg-accent hover:bg-accent-soft"
                    >
                      <Link to="/auth?tab=signup">Get Started</Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </nav>

      {/* Floating social proof widget */}
      {/* <SocialProofNotifications /> */}
    </>
  );
};

export default Navigation;
